import 'regenerator-runtime/runtime'; // https://github.com/redux-saga/redux-saga/issues/280
import { fork, call, put, cancelled } from 'redux-saga/effects';
import { delay } from 'redux-saga';

/*
  Hover utilities to remove boileplate code around api requests

  calling `apiRequestEffect` will fire off begin and end life cycle actions for your requests
  and standardize the shape of the payload to be dumped into state

  capture these in reducers or use the helper method `apiRequestActionHandlers`

  see `getJobDetailsSaga` for an example
*/

export const STATUS = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

export const apiActionTypes = {
  begin: (type) => `${type}_BEGIN`,
  end: (type) => `${type}_END`,
};

/* USAGE:

  function * getCatDetailsSaga (action) {
    const { payload: { catId } } = action
    const { error } = yield apiRequestEffect(action, api.getCatDetails, catId)
    // do some logic with errors here if you want
  }

*/
export function apiRequestEffect(action, func, ...args) {
  return call(apiRequestSaga, action, null, func, ...args);
}

export function apiRequestEffectWithRetries(action, opts = {}, func, ...args) {
  const mergedOpts = {
    retryCount: 4,
    delay: 250,
    ...opts,
  };
  return call(apiRequestSaga, action, mergedOpts, func, ...args);
}

export function* apiRequestSaga(action, opts = {}, func, ...args) {
  const mergedOpts = {
    retryCount: 1,
    delay: 0,
    ...opts,
  };
  // dispatch the `_BEGIN` action to store
  yield put({
    ...action,
    type: apiActionTypes.begin(action.type),
    payload: { ...action.payload, status: STATUS.PENDING },
  });

  let result;
  let status;
  let error;
  for (let i = 0; i < mergedOpts.retryCount; i++) {
    try {
      // call API request function with args
      const response = yield call(func, ...args);
      status = STATUS.SUCCESS;
      error = undefined;
      result = {
        data: response.data,
        statusCode: response.status,
        status,
        error,
      };
      break;
    } catch (e) {
      status = STATUS.FAILURE;
      error = e;
      const errorResponse = e.response || {}; // network errors will return empty response
      result = {
        data: errorResponse.data,
        statusCode: errorResponse.status,
        status,
        error,
      };
      yield delay(mergedOpts.delay);
    }
  }

  if (yield cancelled()) {
    // TODO: @matthewjf abort request
  } else if (result) {
    // dispatch `_END` action with result object assigned to the payload
    yield put({
      ...action,
      type: apiActionTypes.end(action.type),
      payload: { ...action.payload, ...result },
    });
  }

  // return both result and error to the calling saga
  return { result, error };
}

/* USAGE:

  handleActions({
    ...apiRequestActionHandlers(GET_CAT_DETAILS, (state, action) => {
      return ({
        ...state,
        catDetails: action.payload,
      })
    })
  })

*/

export const apiRequestActionHandlers = (actions, stateGenerator) => {
  if (Array.isArray(actions)) {
    let res = {};
    actions.forEach(
      (type) =>
        (res = { ...res, ..._buildActionHandlers(type, stateGenerator) }),
    );
    return res;
  }
  return _buildActionHandlers(actions, stateGenerator);
};

const _buildActionHandlers = (actionType, stateGenerator) => ({
  [apiActionTypes.begin(actionType)]: stateGenerator,
  [apiActionTypes.end(actionType)]: stateGenerator,
});
